import { Icon } from '@iconify/react'
import React, { useEffect } from 'react'

interface DialogProps {
    /** Controls the visibility of the dialog */
    isOpen: boolean
    /** Function to close the dialog */
    onClose: () => void
    /** License details including title, image, and product code */
    license: {
        /** Title of the license */
        title: string
        /** Image source URL of the license */
        imageSrc: string
        /** Product code or part number */
        productCode: string
    }
    /** Optional flag to indicate if this is an evaluation license */
    isEvaluation?: boolean
}

// Dynamic product content mapping
const academicProductContent: Record<string, string[]> = {
    'ENVIDLTLA': [
        '1-year Term license includes software maintenance.',
        'Desktop standalone software.',
        'Windows/Linux support.',
        'ENVI® <a href="https://www.nv5geospatialsoftware.com/Products/ENVI" target="_blank" rel="noopener noreferrer" class="text-primary underline">https://www.nv5geospatialsoftware.com/Products/ENVI</a> offers intuitive data visualization, processing, and analysis.</a> ENVI is beneficial for everyone from entry-level analysts to domain experts in remote sensing image processing.',
        'It contains many easy-to-use tools that don’t require advanced training and knowledge of remote sensing to get actionable results.',
        `ENVI+IDL includes IDL so you can write custom code extensions.`
    ],
    'ENVTLA': [
        '1-year Term license includes software maintenance.',
        'Desktop standalone software.',
        'Windows/Linux support.',
        'ENVI® <a href="https://www.nv5geospatialsoftware.com/Products/ENVI" target="_blank" rel="noopener noreferrer" class="text-primary underline">https://www.nv5geospatialsoftware.com/Products/ENVI</a> offers intuitive data visualization, processing, and analysis.</a> ENVI is beneficial for everyone from entry-level analysts to domain experts in remote sensing image processing.',
        'It contains many easy-to-use tools that don’t require advanced training and knowledge of remote sensing to get actionable results.'
    ],
    'ENVPROALIC': [
        'Perpetual license includes 1-year software maintenance.',
        'Desktop standalone software.',
        'Windows/Linux support.',
        'ENVI Pro is a bundle that includes ENVI+IDL, ENVI Agent, ENVI Catalog, Atmospheric Correction, Deep Learning.',
        'Note: ENVI Pro Academic includes ENVI+IDL, ENVI Agent, ENVI Catalog, Atmospheric Correction, Crop Science, Deep Learning, DEM Extraction, Feature Extraction.'
    ],
    'ENVPROATLA': [
        'Perpetual license includes 1-year software maintenance.',
        'Desktop standalone software.',
        'Windows/Linux support.',
        'Note: ENVI Pro Academic includes ENVI+IDL, ENVI Agent, ENVI Catalog, Atmospheric Correction, Crop Science, Deep Learning, DEM Extraction, Feature Extraction. ENVI Agent is only available for academic licenses via ENVI Pro Academic.'
    ],
    'ENVPROTLA': [
        '1-year Term license includes software maintenance.',
        'Desktop standalone software.',
        'Windows/Linux support.',
        'ENVI Pro is a bundle that includes ENVI+IDL, ENVI Agent, ENVI Catalog, Atmospheric Correction, Deep Learning.',
        'Note: ENVI Pro Academic includes ENVI+IDL, ENVI Agent, ENVI Catalog, Atmospheric Correction, Crop Science, Deep Learning, DEM Extraction, Feature Extraction.'
    ],
    'ENVACMTLA': [
        '1-year Term license includes software maintenance.',
        'Desktop module software requires ENVI+IDL or ENVI.',
        'Windows/Linux support.',
        'ENVI® Atmospheric Correction Module (ACM) <a href="https://www.nv5geospatialsoftware.com/Products/ENVI#spectral" target="_blank" rel="noopener noreferrer" class="text-primary underline">https://www.nv5geospatialsoftware.com/Products/ENVI#spectral</a> enables correction for atmospheric interference in multi- and hyperspectral imagery. Use either a one-click, scene-based reflectance correction (QUAC®) or a Modtran® model-based surface-reflectance correction (FLAASH®). For change detection applications, ACM ensures that you are comparing like imagery to achieve accurate results.'
    ],
    'IDLTLA': [
        '1-year Term license includes software maintenance.',
        'Desktop standalone software.',
        'Windows/Mac OS/Linux support.',
        'IDL® <a href="https://www.nv5geospatialsoftware.com/Products/IDL" target="_blank" rel="noopener noreferrer" class="text-primary underline">https://www.nv5geospatialsoftware.com/Products/IDL</a> is the trusted scientific programming language</a> used across disciplines to create meaningful visualizations out of complex numerical data.',
        'IDL is a cross-platform solution, providing advanced visualization and analytical tools for solving a wide variety of problems from small-scale analysis programs to widely deployed applications.'
    ],
    'SUPUNL': ['Academic licenses include online blog support. Email or live Technical Support is extra.'],
    'ELNISRS': [
        'Introduction to Spectral Remote Sensing - Designed to expose learners to the common applications of spectral analysis. <a href="https://www.nv5geospatialsoftware.com/Learn/Training/eLearning/Introduction-To-Remote-Sensing" target="_blank" rel="noopener noreferrer" class="text-primary underline">NV5 eLearning | Introduction To Remote Sensing</a>.'
    ],
    'ELNEFUN': [
        'ENVI® Fundamentals - Designed for those who are new to ENVI software or need a refresher on ENVI\'s capabilities. <a href="https://www.nv5geospatialsoftware.com/Learn/Training/eLearning/ENVI-Fundamentals" target="_blank" rel="noopener noreferrer" class="text-primary underline">eLearning Path 4 | ENVI Fundamentals</a>.'
    ],
    'ELNEBG': [
        'ENVI® Basics for GEOINT - A scenario-based introduction to ENVI, designed to get defense and intelligence image analysts, geospatial engineers, and geospatial analysts up to speed as quickly as possible. <a href="https://www.nv5geospatialsoftware.com/Learn/Training/eLearning/ENVI-Basics-For-GEOINT" target="_blank" rel="noopener noreferrer" class="text-primary underline">NV5 eLearning | ENVI Basics For GEOINT</a>.'
    ],
    'ELNAEG': [
        'Advanced ENVI® for GEOINT - Designed for analysts and geospatial engineers wanting to produce advanced products to support defense and intelligence operations. <a href="https://www.nv5geospatialsoftware.com/Learn/Training/eLearning/Advanced-ENVI-For-GEOINT" target="_blank" rel="noopener noreferrer" class="text-primary underline">NV5 eLearning | Advanced ENVI For GEOINT</a>.'
    ]
}

const commercialProductContent: Record<string, string[]> = {
    'ENVPROLIC': [
        'Perpetual license includes 1-year software maintenance.',
        'Desktop standalone software.',
        'Windows/Linux support.',
        'ENVI Pro is a bundle that includes ENVI+IDL, ENVI Agent, ENVI Catalog, Atmospheric Correction, Deep Learning.'
    ],
    'ENVPROTLA': [
        'Perpetual license includes 1-year software maintenance.',
        'Desktop standalone software.',
        'Windows/Linux support.',
        'ENVI Pro is a bundle that includes ENVI+IDL, ENVI Agent, ENVI Catalog, Atmospheric Correction, Deep Learning.'
    ],
    'ENVAGTLIC': [
        'Perpetual license includes 1-year software maintenance.',
        'Desktop module software requires ENVI+IDL (or ENVI Pro).',
        'Windows/Linux support.',
        'ENVI Agent is an AI-powered geospatial partner to help plan and execute ENVI processing workflows.'
    ],
    'ENVAGTTLA': [
        'Perpetual license includes 1-year software maintenance.',
        'Desktop module software requires ENVI+IDL (or ENVI Pro).',
        'Windows/Linux support.',
        'ENVI Agent is an AI-powered geospatial partner to help plan and execute ENVI processing workflows.'
    ],
    'ENVIDLLIC': [
        'Perpetual license includes 1-year software maintenance.',
        'Desktop standalone software.',
        'Windows/Linux support.',
        'ENVI® <a href="https://www.nv5geospatialsoftware.com/Products/ENVI" target="_blank" rel="noopener noreferrer" class="text-primary underline">https://www.nv5geospatialsoftware.com/Products/ENVI</a> offers intuitive data visualization, processing, and analysis. ENVI is beneficial for everyone from entry-level analysts to domain experts in remote sensing image processing.',
        'It contains many easy-to-use tools that don’t require advanced training and knowledge of remote sensing to get actionable results.',
        'ENVI+IDL includes IDL so you can write custom code extensions.'
    ],
    'ENVIDLTLA': [
        '1-year Term license includes software maintenance.',
        'Desktop standalone software.',
        'Windows/Linux support.',
        'ENVI® <a href="https://www.nv5geospatialsoftware.com/Products/ENVI" target="_blank" rel="noopener noreferrer" class="text-primary underline">https://www.nv5geospatialsoftware.com/Products/ENVI</a> offers intuitive data visualization, processing, and analysis. ENVI is beneficial for everyone from entry-level analysts to domain experts in remote sensing image processing.',
        'It contains many easy-to-use tools that don’t require advanced training and knowledge of remote sensing to get actionable results.',
        'ENVI+IDL includes IDL so you can write custom code extensions.'
    ],
    'ENVLIC': [
        'Perpetual license includes 1-year software maintenance.',
        'Desktop standalone software.',
        'Windows/Linux support.',
        'ENVI® <a href="https://www.nv5geospatialsoftware.com/Products/ENVI" target="_blank" rel="noopener noreferrer" class="text-primary underline">https://www.nv5geospatialsoftware.com/Products/ENVI</a> offers intuitive data visualization, processing, and analysis. ENVI is beneficial for everyone from entry-level analysts to domain experts in remote sensing image processing.',
        'It contains many easy-to-use tools that don’t require advanced training and knowledge of remote sensing to get actionable results.'
    ],
    'ENVTLA': [
        '1-year Term license includes software maintenance.',
        'Desktop standalone software.',
        'Windows/Linux support.',
        'ENVI® <a href="https://www.nv5geospatialsoftware.com/Products/ENVI" target="_blank" rel="noopener noreferrer" class="text-primary underline">https://www.nv5geospatialsoftware.com/Products/ENVI</a> offers intuitive data visualization, processing, and analysis. ENVI is beneficial for everyone from entry-level analysts to domain experts in remote sensing image processing.',
        'It contains many easy-to-use tools that don’t require advanced training and knowledge of remote sensing to get actionable results.'
    ],
    'ENVACMLIC': [
        'Perpetual license includes 1-year software maintenance.',
        'Desktop module software requires ENVI+IDL or ENVI.',
        'Windows/Linux support.',
        'ENVI® Atmospheric Correction Module (ACM) <a href="https://www.nv5geospatialsoftware.com/Products/ENVI#spectral" target="_blank" rel="noopener noreferrer" class="text-primary underline">https://www.nv5geospatialsoftware.com/Products/ENVI#spectral</a> enables correction for atmospheric interference in multi- and hyperspectral imagery. Use either a one-click, scene-based reflectance correction (QUAC®) or a Modtran® model-based surface-reflectance correction (FLAASH®). For change detection applications, ACM ensures that you are comparing like imagery to achieve accurate results.'
    ],
    'ENVACMTLA': [
        '1-year Term license includes software maintenance.',
        'Desktop module software requires ENVI+IDL or ENVI.',
        'Windows/Linux support.',
        'ENVI® Atmospheric Correction Module (ACM) <a href="https://www.nv5geospatialsoftware.com/Products/ENVI#spectral" target="_blank" rel="noopener noreferrer" class="text-primary underline">https://www.nv5geospatialsoftware.com/Products/ENVI#spectral</a> enables correction for atmospheric interference in multi- and hyperspectral imagery. Use either a one-click, scene-based reflectance correction (QUAC®) or a Modtran® model-based surface-reflectance correction (FLAASH®). For change detection applications, ACM ensures that you are comparing like imagery to achieve accurate results.'
    ],
    'IDLLIC': [
        'Perpetual license includes 1-year software maintenance.',
        'Desktop standalone software.',
        'Windows/Mac OS/Linux support.',
        'IDL® <a href="https://www.nv5geospatialsoftware.com/Products/IDL" target="_blank" rel="noopener noreferrer" class="text-primary underline">https://www.nv5geospatialsoftware.com/Products/IDL</a> is the trusted scientific programming language used across disciplines to create meaningful visualizations out of complex numerical data.',
        'IDL is a cross-platform solution, providing advanced visualization and analytical tools for solving a wide variety of problems from small-scale analysis programs to widely deployed applications.'
    ],
    'IDLTLA': [
        '1-year Term license includes software maintenance.',
        'Desktop standalone software.',
        'Windows/Mac OS/Linux support.',
        'IDL® <a href="https://www.nv5geospatialsoftware.com/Products/IDL" target="_blank" rel="noopener noreferrer" class="text-primary underline">https://www.nv5geospatialsoftware.com/Products/IDL</a> is the trusted scientific programming language used across disciplines to create meaningful visualizations out of complex numerical data.',
        'IDL is a cross-platform solution, providing advanced visualization and analytical tools for solving a wide variety of problems from small-scale analysis programs to widely deployed applications.'
    ],
    'ELNISRS': [
        'Introduction to Spectral Remote Sensing - Designed to expose learners to the common applications of spectral analysis. <a href="https://www.nv5geospatialsoftware.com/Learn/Training/eLearning/Introduction-To-Remote-Sensing" target="_blank" rel="noopener noreferrer" class="text-primary underline">NV5 eLearning | Introduction To Remote Sensing</a>.'
    ],
    'ELNEFUN': [
        'ENVI® Fundamentals - Designed for those who are new to ENVI software or need a refresher on ENVI\'s capabilities. <a href="https://www.nv5geospatialsoftware.com/Learn/Training/eLearning/ENVI-Fundamentals" target="_blank" rel="noopener noreferrer" class="text-primary underline">eLearning Path 4 | ENVI Fundamentals</a>.'
    ],
    'ELNEBG': [
        'ENVI® Basics for GEOINT - A scenario-based introduction to ENVI, designed to get defense and intelligence image analysts, geospatial engineers, and geospatial analysts up to speed as quickly as possible. <a href="https://www.nv5geospatialsoftware.com/Learn/Training/eLearning/ENVI-Basics-For-GEOINT" target="_blank" rel="noopener noreferrer" class="text-primary underline">NV5 eLearning | ENVI Basics For GEOINT</a>.'
    ],
    'ELNAEG': [
        'Advanced ENVI® for GEOINT - Designed for analysts and geospatial engineers wanting to produce advanced products to support defense and intelligence operations. <a href="https://www.nv5geospatialsoftware.com/Learn/Training/eLearning/Advanced-ENVI-For-GEOINT" target="_blank" rel="noopener noreferrer" class="text-primary underline">NV5 eLearning | Advanced ENVI For GEOINT</a>.'
    ],
    'ELNSEG': [
        'ENVI SAR Essentials, designed for GEOINT Imagery Analysts, is a scenario-based course that demonstrates how to visually interpret and create operational products from SAR imagery for defense and intelligence purposes. <a href="https://www.nv5geospatialsoftware.com/Learn/Training/eLearning/SAR-Essentials-for-GEOINT" target="_blank" rel="noopener noreferrer" class="text-primary underline">SAR Essentials for GEOINT | eLearning</a>.'
    ],
    'ELNHAM': [
        'Hyperspectral Analysis of Minerals provides an introduction to the use of imaging spectrometry in geologic and mineralogic applications. <a href="https://www.nv5geospatialsoftware.com/Learn/Training/eLearning/Hyperspectral-Analysis-of-Minerals" target="_blank" rel="noopener noreferrer" class="text-primary underline">Hyperspectral Analysis of Minerals | eLearning</a>.'
    ]
}

// Evaluation product content mapping with correct API product codes
const evaluationProductContent: Record<string, string[]> = {
    'ENVEVLTLA': [
        'For temporary trial-use, ENVI® <a href="https://www.nv5geospatialsoftware.com/Products/ENVI" target="_blank" rel="noopener noreferrer" class="text-primary underline">https://www.nv5geospatialsoftware.com/Products/ENVI</a> offers intuitive data visualization, processing, and analysis.'
    ],
    'IDLEVLTLA': [
        'For temporary trial-use, IDL® <a href="https://www.nv5geospatialsoftware.com/Products/IDL" target="_blank" rel="noopener noreferrer" class="text-primary underline">https://www.nv5geospatialsoftware.com/Products/IDL</a> is the trusted scientific programming language used across disciplines to create meaningful visualizations out of complex numerical data.'
    ],
    'SARESSEVLTLA': [
        'For temporary trial-use, ENVI® SAR Essentials <a href="https://www.nv5geospatialsoftware.com/Solutions/SAR-Solutions#essentials" target="_blank" rel="noopener noreferrer" class="text-primary underline">https://www.nv5geospatialsoftware.com/Solutions/SAR-Solutions#essentials</a> features streamlined SAR workflows for analysts and operational users.It automates and simplifies SAR data processing and analysis for the most common SAR applications.'
    ],
    'SAREVLTLA': [
        'For temporary trial-use, ENVI® SARscape <a href="https://www.nv5geospatialsoftware.com/Solutions/SAR-Solutions#sarscape" target="_blank" rel="noopener noreferrer" class="text-primary underline">https://www.nv5geospatialsoftware.com/Solutions/SAR-Solutions#sarscape</a> is designed for those advanced users who are knowledgeable about SAR data and processing and enables users to modify parameters to create the exact products they need.It supports all commercially available SAR datasets, including SICD, and is optimized for handling data of any size.'
    ],
    'IIQEVLTLA': [
        'For temporary trial-use, ImageIQ <a href="https://www.nv5geospatialsoftware.com/Products/ImageIQ" target="_blank" rel="noopener noreferrer" class="text-primary underline">https://www.nv5geospatialsoftware.com/Products/ImageIQ</a> is a powerful photogrammetry suite that processes images captured by drones or aerial platforms to generate geospatial products.'
    ]
}

/**
 * LicenseDetailsDialog Component
 *
 * A modal dialog displaying license details dynamically based on the product code.
 *
 * @component
 * @param {DialogProps} props - The properties passed to the component.
 * @returns {JSX.Element | null} A modal dialog for displaying license details.
 */
export const LicenseDetailsDialog = ({ isOpen, onClose, license, isEvaluation = false }: DialogProps) => {
    const isStudentUser = localStorage.getItem('isStudentUser')
    const academicFlow = isStudentUser === 'true'
    const commercialFlow = isStudentUser === 'false'

    let content: string[] = []

    if (isEvaluation) {
        // Use evaluation content for evaluation licenses
        content = evaluationProductContent[license.productCode] || [
            'For temporary trial-use, this evaluation license provides access to the software for testing and evaluation purposes.',
            'Evaluation licenses are time-limited and intended for trial use only.',
            'For full license information, please contact our sales team.'
        ]
    } else {
        // Use existing logic for academic/commercial flows
        content = academicFlow
            ? academicProductContent[license.productCode] || []
            : commercialFlow
              ? commercialProductContent[license.productCode] || []
              : []
    }

    useEffect(() => {
        /**
         * Handles the 'Escape' key press to close the dialog.
         * @param {KeyboardEvent} event - The keyboard event.
         */
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onClose()
            }
        }

        if (isOpen) {
            document.addEventListener('keydown', handleEscape)
            document.body.style.overflow = 'hidden'
        }

        return () => {
            document.removeEventListener('keydown', handleEscape)
            document.body.style.overflow = 'unset'
        }
    }, [isOpen, onClose])

    if (!isOpen) return null
    /**
     * Prevents closing the dialog when clicking inside the modal.
     * @param {React.MouseEvent} event - The mouse event.
     */
    const handleBackdropClick = (event: React.MouseEvent) => {
        event.stopPropagation()
    }

    return (
        <div className='fixed inset-0 z-50 flex items-center justify-center'>
            {/* Backdrop */}
            <div className='fixed inset-0 bg-black/50' onClick={handleBackdropClick} aria-hidden='true' />

            {/* Dialog */}
            <div
                className='relative z-50 w-full max-w-3xl max-h-[90vh] bg-white rounded-lg shadow-xl overflow-y-auto'
                role='dialog'
                aria-modal='true'
            >
                {/* Header */}
                <div className='sticky top-0 bg-white flex items-center justify-between p-6 pb-0'>
                    <h2 className='text-xl font-semibold'>{license?.title}</h2>
                    <Icon
                        icon={'material-symbols-light:close-rounded'}
                        height={30}
                        width={30}
                        onClick={onClose}
                        className='text-danger cursor-pointer'
                        aria-label='Close dialog'
                    />
                </div>

                {/* Content */}
                <div className='p-6'>
                    <div className='border-2 border-gray rounded-lg flex'>
                        <div className='w-full text-left p-6 overflow-y-auto max-h-[70vh]'>
                            <ul className='list-disc list-inside space-y-2 text-secondary'>
                                {content.length > 0 ? (
                                    content.map((line, index) => <li key={index} dangerouslySetInnerHTML={{ __html: line }}></li>)
                                ) : (
                                    <li>No product details available. Please contact support for more information.</li>
                                )}
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
